// shop-frontend/src/context/ShopMallCartContext.js
import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const ShopMallCartContext = createContext();

export function useShopMallCart() {
  const ctx = useContext(ShopMallCartContext);
  if (!ctx) throw new Error("useShopMallCart must be used within ShopMallCartProvider");
  return ctx;
}

function getMallCartKey() {
  try {
    // Use shopToken-based cart to keep it separate from user cart
    const shopToken = localStorage.getItem("shopToken");
    if (!shopToken) return "mallCart_guest";
    
    // Decode shop ID from token (simple base64 decode of payload)
    const parts = shopToken.split(".");
    if (parts.length >= 2) {
      const payload = JSON.parse(atob(parts[1]));
      const shopId = payload.shopId || payload.id || payload.userId || "guest";
      return `mallCart_${shopId}`;
    }
    return "mallCart_guest";
  } catch {
    return "mallCart_guest";
  }
}

function readMallCart() {
  try {
    const key = getMallCartKey();
    return JSON.parse(localStorage.getItem(key) || "[]");
  } catch {
    return [];
  }
}

function writeMallCart(items) {
  try {
    const key = getMallCartKey();
    localStorage.setItem(key, JSON.stringify(items || []));
  } catch (err) {
    console.error("Failed to save mall cart:", err);
  }
}

export function ShopMallCartProvider({ children }) {
  const [items, setItems] = useState(() => readMallCart());
  const [isOpen, setIsOpen] = useState(false);

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    writeMallCart(items);
  }, [items]);

  // Calculate total count
  const count = items.reduce((sum, item) => sum + Math.max(1, Number(item.qty) || 1), 0);

  // Calculate total price
  const total = items.reduce((sum, item) => {
    const qty = Math.max(1, Number(item.qty) || 1);
    const price = Number(item.price) || 0;
    return sum + price * qty;
  }, 0);

  const addToCart = useCallback((product, quantity = 1) => {
    setItems((prev) => {
      // Use cartId (variant-unique) for deduplication; fallback to productId/_id
      const incomingCartId = product.cartId || String(product._id || product.id);
      const existing = prev.find((item) =>
        String(item.cartId || item.productId || item._id) === incomingCartId
      );

      if (existing) {
        // Same variant already in cart — bump qty
        return prev.map((item) => {
          if (String(item.cartId || item.productId || item._id) === incomingCartId) {
            const newQty = Math.max(1, (item.qty || 1) + quantity);
            return { ...item, qty: newQty };
          }
          return item;
        });
      } else {
        // New item (or new variant)
        const newItem = {
          cartId: incomingCartId,
          productId: product.productId || product._id || product.id,
          _id: incomingCartId,
          title: product.title || product.name || "Product",
          price: product.price || product.localPrice || 0,
          currency: product.currency || "USD",
          image: product.image || product.imageUrl || product.thumbnail || "",
          shopId: product.shopId || product.shop?._id || product.shop?.id || "",
          shopName: product.shopName || product.shop?.shopName || product.shop?.name || "Shop",
          variantLabel: product.variantLabel || "",
          variantId: product.variantId || "",
          qty: Math.max(1, quantity),
        };
        return [...prev, newItem];
      }
    });
  }, []);

  const removeFromCart = useCallback((cartId) => {
    setItems((prev) =>
      prev.filter((item) => String(item.cartId || item.productId || item._id) !== String(cartId))
    );
  }, []);

  const updateQuantity = useCallback((cartId, newQty) => {
    if (newQty <= 0) {
      removeFromCart(cartId);
      return;
    }

    setItems((prev) =>
      prev.map((item) => {
        if (String(item.cartId || item.productId || item._id) === String(cartId)) {
          return { ...item, qty: Math.max(1, newQty) };
        }
        return item;
      })
    );
  }, [removeFromCart]);

  const clearCart = useCallback(() => {
    setItems([]);
  }, []);

  const openCart = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeCart = useCallback(() => {
    setIsOpen(false);
  }, []);

  const value = {
    items,
    count,
    total,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    isOpen,
    openCart,
    closeCart,
  };

  return (
    <ShopMallCartContext.Provider value={value}>
      {children}
    </ShopMallCartContext.Provider>
  );
}
